"use client";

import { captureEvent } from "@/app/_analytics/analytics";
import { usePathname } from "@/i18n/navigation";
import { EventName } from "@mw/shared/analyticsEventNames";
import { createContext, useCallback, useContext, useMemo, useState } from "react";

import { getPostAuthRedirectUrl } from "./utils/redirects";

export type AuthDialogView = "sign-up" | "sign-in" | "sso" | "forgot-password";

export interface AuthDialogOptions {
  redirectTo?: string;
  onSuccess?: (isNewUser: boolean) => void;
  initialView?: AuthDialogView;
}

interface AuthDialogContextValue {
  isOpen: boolean;
  view: AuthDialogView;
  openAuthDialog: (opts?: AuthDialogOptions) => void;
  closeAuthDialog: () => void;
  switchView: (view: AuthDialogView) => void;
  handleAuthSuccess: (isNewUser: boolean) => void;
  verificationContinueUrl: string;
}

const AuthDialogContext = createContext<AuthDialogContextValue | null>(null);

export function AuthDialogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<AuthDialogView>("sign-up");
  const [postAuthConfig, setPostAuthConfig] = useState<{
    redirectTo?: string;
    onSuccess?: (isNewUser: boolean) => void;
  }>({});

  const openAuthDialog = useCallback((opts?: AuthDialogOptions) => {
    const view = opts?.initialView ?? "sign-up";
    setView(view);
    setPostAuthConfig({ redirectTo: opts?.redirectTo, onSuccess: opts?.onSuccess });
    setIsOpen(true);
    captureEvent(EventName.mw_auth_dialog_opened, { view });
  }, []);

  const closeAuthDialog = useCallback(() => {
    setIsOpen(false);
    captureEvent(EventName.mw_auth_dialog_closed);
  }, []);

  const switchView = useCallback((v: AuthDialogView) => {
    setView(v);
  }, []);

  const handleAuthSuccess = useCallback(
    (isNewUser: boolean) => {
      closeAuthDialog();

      if (postAuthConfig.onSuccess) {
        postAuthConfig.onSuccess(isNewUser);
        return;
      }

      window.location.href = getPostAuthRedirectUrl({ isNewUser, redirectTo: postAuthConfig.redirectTo, pathname });
    },
    [closeAuthDialog, postAuthConfig, pathname],
  );

  const verificationContinueUrl = useMemo(
    () => getPostAuthRedirectUrl({ isNewUser: true, redirectTo: postAuthConfig.redirectTo, pathname }),
    [postAuthConfig.redirectTo, pathname],
  );

  const value = useMemo(
    () => ({ isOpen, view, openAuthDialog, closeAuthDialog, switchView, handleAuthSuccess, verificationContinueUrl }),
    [isOpen, view, openAuthDialog, closeAuthDialog, switchView, handleAuthSuccess, verificationContinueUrl],
  );

  return <AuthDialogContext.Provider value={value}>{children}</AuthDialogContext.Provider>;
}

export function useAuthDialogContext() {
  const context = useContext(AuthDialogContext);
  if (!context) {
    throw new Error("useAuthDialogContext must be used within an AuthDialogProvider");
  }
  return context;
}
